import _ from 'lodash'
import type { Data, TextTheme } from '@wix/thunderbolt-becky-types'
import type { AnywhereThemeOverride } from '@wix/thunderbolt-ssr-api'
import type { ColorOverrides, TextOverrides } from '@wix/thunderbolt-commons'
import { parseThemeOverrides, textPresetKeys } from '@wix/thunderbolt-commons'
import { fontObjectToString, parseFontStr } from '@wix/thunderbolt-becky-root'

export const applyAnywhereThemeOverride = ({
	data,
	anywhereThemeOverride,
}: {
	data: Data
	anywhereThemeOverride: string
}): Data => {
	const parsedOverrides: AnywhereThemeOverride = parseThemeOverrides(anywhereThemeOverride)

	const { color: originalColor, font: originalFont, textTheme: originalTextTheme } = data.theme_data.THEME_DATA

	const { colorOverrides, textOverrides } = parsedOverrides
	const colorWithOverrides = applyColorOverrides(originalColor, colorOverrides)
	const fontWithOverrides = applyFontOverrides(originalFont, textOverrides)
	const textThemeWithOverrides = originalTextTheme && applyTextThemeOverrides(originalTextTheme, textOverrides)

	const dataOverrides = {
		theme_data: {
			THEME_DATA: { color: colorWithOverrides, font: fontWithOverrides, textTheme: textThemeWithOverrides },
		},
	}

	return _.merge({}, data, dataOverrides)
}

const applyColorOverrides = (originalColors: Array<string>, overrides: ColorOverrides): Array<string> => {
	const colorsWithOverrides = originalColors.map((color, index) => {
		const override = overrides?.[`color_${index}`]?.hexCode
		return override ?? color
	})

	return colorsWithOverrides
}

const applyFontOverrides = (originalFont: Array<string>, overrides: TextOverrides): Array<string> => {
	const fontsWithOverrides = originalFont.map((font, index) => {
		const textPreset = textPresetKeys[index]
		const override = overrides?.[textPreset]

		if (!override) {
			return font
		}

		const parsedFont = parseFontStr(font)

		const fontObjectWithOverrides = {
			...parsedFont,
			style: override.style ?? parsedFont.style,
			variant: override.variant ?? parsedFont.variant,
			weight: override.weight ?? parsedFont.weight,
			size: Number(override.size) || parsedFont.size,
			lineHeight: override.lineHeight ?? parsedFont.lineHeight,
			family: override.family ? [override.family] : parsedFont.family,
		}

		const fontStringWithOverrides = fontObjectToString(fontObjectWithOverrides)
		return fontStringWithOverrides
	})

	return fontsWithOverrides
}

const applyTextThemeOverrides = (originalTextTheme: Array<TextTheme>, overrides: TextOverrides): Array<TextTheme> => {
	const textThemeWithOverrides = originalTextTheme.map((textTheme, index) => {
		const textPreset = textPresetKeys[index]
		const override = overrides?.[textPreset]

		if (!override) {
			return textTheme
		}

		const textThemeOverrides: Partial<TextTheme> = {
			fontStyle: override.style,
			fontWeight: override.weight,
			fontVariant: override.variant,
			fontFamily: override.family,
			fontSize: override.size ? `${override.size}px` : undefined,
			lineHeight: override.lineHeight,
			letterSpacing: override.letterSpacing,
		}

		return _.merge({}, textTheme, textThemeOverrides)
	})

	return textThemeWithOverrides
}
